'use client';

import { pdfjs } from 'react-pdf';
import {
  Button,
  Dialog,
  Pill,
  PDFControls,
  ButtonGroup,
  Icon,
  Flex,
  Text,
  Heading,
} from '@/_components';
import { PDFViewer } from '@/_components/PDFViewer';
import { useContext, useState, useEffect, useCallback, type FC } from 'react';
import { PreviewContext } from '@/_contexts/PreviewProvider';
import { ReasonDialog } from './_components/ReasonDialog';
import { getClasses } from '@/_utils/classnames';
import {
  approveTaxReturn,
  requestChanges,
  downloadTaxReturn,
} from '@/_api/admin-tax-returns';
import { ToastContext } from '@/_contexts/ToastProvider';
import { UserContext } from '@/_contexts/UserProvider';
import { downloadDocument } from '@/_api/client-documents';
import styles from './DraftTaxReturnReviewDialog.module.css';
import { DraftTaxReturnReviewDialogProps } from './DraftTaxReturnReviewDialog.types';
import { BookmarkItem } from './_components/BookmarkItem';
import { _downloadBlob } from '@/_api/api';
import type { Bookmark } from '@/_types/taxReturnDelivery';
import { FirmSettingsContext } from '@/_contexts/FirmSettingsProvider';

export const DraftTaxReturnReviewDialog: FC<
  DraftTaxReturnReviewDialogProps
> = ({ isReviewed, onClose }) => {
  const [showMenu, setShowMenu] = useState<boolean>(false);

  const {
    setRotationDegrees,
    setPreviewAnnotations,
    setPreviewCompanyIdForAnnotations,
    selectedTaxReturn,
    setSelectedTaxReturn,
  } = useContext(PreviewContext);
  const { addToast } = useContext(ToastContext);
  const { token } = useContext(UserContext);
  const { preferences } = useContext(FirmSettingsContext);
  const [pdfUrl, setPdfUrl] = useState<string>();
  const [bookmarks, setBookmarks] = useState<Bookmark[]>([]);
  const [isLoadingBookmarks, setIsLoadingBookmarks] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [selectedBookmark, setSelectedBookmark] = useState<number | null>(null);
  const [showReasonDialog, setShowReasonDialog] = useState(false);

  const extractBookmarks = useCallback(
    async (pdfSrc: string) => {
      try {
        setIsLoadingBookmarks(true);

        const loadingTask = pdfjs.getDocument({ url: pdfSrc });
        const pdf = await loadingTask.promise;

        const outline = await pdf.getOutline();
        if (!outline) {
          setBookmarks([]);
          return;
        }

        const processBookmarks = async (items: any[]): Promise<Bookmark[]> => {
          const processed = await Promise.all(
            items.map(async (bookmark) => {
              let pageIndex = null;
              if (
                bookmark?.dest &&
                bookmark?.dest.length > 0 &&
                bookmark?.dest[0]
              ) {
                try {
                  const resolvedPageIndex = await pdf.getPageIndex(
                    bookmark?.dest[0],
                  );
                  pageIndex = resolvedPageIndex + 1;
                } catch (error) {
                  console.warn(
                    `Failed to resolve page index for: ${bookmark?.title}`,
                    error,
                  );
                }
              }

              return {
                title: bookmark?.title,
                pageIndex,
                items: bookmark?.items
                  ? await processBookmarks(bookmark?.items)
                  : [],
              };
            }),
          );

          return processed;
        };

        let processedBookmarks = await processBookmarks(outline);
        if (processedBookmarks.length === 1) {
          processedBookmarks = processedBookmarks[0].items;
        }

        setBookmarks(processedBookmarks);
      } catch (error) {
        console.error('Error extracting bookmarks:', error);
        addToast('error', 'Failed to load PDF bookmarks');
      } finally {
        setIsLoadingBookmarks(false);
      }
    },
    [addToast],
  );

  useEffect(() => {
    const downloadPdf = async () => {
      if (!selectedTaxReturn?.watermarked_file) {
        console.log('No watermarked file available');
        return;
      }

      try {
        const { document: doc } = await downloadDocument({
          companyId: selectedTaxReturn.company_id,
          link: selectedTaxReturn.watermarked_file,
          token,
        });
        const downloadedUrl = doc?.data?.link;

        if (!downloadedUrl) {
          console.error('No URL received from downloadDocument');
          addToast('error', 'Failed to load PDF file');
          return;
        }

        setPdfUrl(downloadedUrl);
        await extractBookmarks(downloadedUrl);
      } catch (error) {
        console.error('Error downloading document:', error);
        addToast('error', 'Failed to load PDF file');
      }
    };

    downloadPdf();
  }, [selectedTaxReturn, token, addToast, extractBookmarks]);

  const onResetPreviewFile = () => {
    setRotationDegrees(0);
    setPdfUrl(undefined);
    setPreviewAnnotations([]);
    setPreviewCompanyIdForAnnotations(undefined);
    setBookmarks([]);
  };

  const handleDownload = async () => {
    if (!selectedTaxReturn) return;

    setIsDownloading(true);

    try {
      const response = await downloadTaxReturn({
        id: selectedTaxReturn.id,
        companyId: selectedTaxReturn.company_id,
        includeOtherPages: false,
        onlySignaturePages: false,
        token,
      });

      if (!response?.link) {
        addToast('error', 'Error downloading tax return draft');
        throw new Error('Download link not found in response');
      }

      if (!preferences?.tmp_trd_download_notification_enabled) {
        await _downloadBlob({
          url: response?.link,
          fileName: `${selectedTaxReturn.tax_return_name} (Draft).pdf`,
        });
      } else {
        addToast(
          'success',
          'You will receive a notification when the tax return is ready to be downloaded.',
        );
      }
    } catch (error) {
      console.error('Error during download:', error);
    } finally {
      setIsDownloading(false);
    }
  };

  const handleClose = () => {
    setSelectedTaxReturn(undefined);
    onResetPreviewFile();
    onClose();
  };

  const handleApprove = async () => {
    if (!selectedTaxReturn) return;

    try {
      const { error } = await approveTaxReturn({
        id: selectedTaxReturn.id,
        company_id: selectedTaxReturn.company_id,
        reason: '',
        token,
      });

      if (error) {
        throw new Error(error);
      }

      addToast('success', 'Tax return approved successfully');
      onResetPreviewFile();
      onClose();
    } catch (error) {
      console.error('Error approving tax return:', error);
      addToast('error', 'Failed to approve tax return', { error });
    }
  };

  const handleRequestChanges = async (reason: string) => {
    if (!selectedTaxReturn) return;

    try {
      const { error } = await requestChanges({
        id: selectedTaxReturn.id,
        company_id: selectedTaxReturn.company_id,
        reason,
        token,
      });

      if (error) {
        throw new Error(error);
      }

      addToast('success', 'Tax return changes requested successfully');
      onResetPreviewFile();
      onClose();
    } catch (error) {
      console.error('Error requesting changes:', error);
      addToast('error', 'Failed to request changes', { error });
    }
  };

  if (!selectedTaxReturn) return null;

  const onCloseMenu = () => setShowMenu(false);
  const onToggleMenu = () => setShowMenu((previous) => !previous);

  const hasBookmarks: boolean = bookmarks.length > 0;

  const draftTaxReturnReviewDialogMenuClasses = getClasses(
    styles.draftTaxReturnReviewDialogMenu,
    {
      [styles.draftTaxReturnReviewDialogMenuOpen]: showMenu,
    },
  );

  const pdfContainerClasses = getClasses(styles.pdfContainer, {
    [styles.pdfContainerWithBookmarks]: hasBookmarks,
  });

  return (
    <>
      <Dialog
        isOpen={!!selectedTaxReturn}
        onClose={handleClose}
        isWide={true}
        disablePadding={true}
        isFullScreen={true}
        title="Review Draft Tax Return"
        headerButtons={
          !isReviewed && (
            <>
              <div className={styles.draftTaxReturnReviewDialog__headerRotate}>
                <PDFControls />
              </div>
              <ButtonGroup>
                <Button
                  value="Download"
                  size="small"
                  isIconOnly={true}
                  intent="white-fill"
                  onClick={handleDownload}
                  withTooltip
                  tooltipPlacement="bottom"
                  tooltip="Download"
                  iconLeft="download"
                  isLoading={isDownloading}
                />
              </ButtonGroup>
            </>
          )
        }
      >
        <Dialog.Content>
          {hasBookmarks && (
            <>
              <Flex
                className={styles.draftTaxReturnReviewDialogMenu__toggle}
                onClick={onToggleMenu}
                isCentered={true}
              >
                <Icon name="list" size={20} />
              </Flex>

              <Flex
                padding={6}
                gap={8}
                className={draftTaxReturnReviewDialogMenuClasses}
                isColumn={true}
              >
                <Heading level={3}>Index</Heading>
                <Text type="small">
                  The index is generated from the PDF&apos;s bookmarks. Click on
                  a page to navigate to it.
                </Text>
                {isLoadingBookmarks ? (
                  <Flex isColumn={true} gap={2}>
                    <Text type="small" color="secondary">
                      Loading bookmarks...
                    </Text>
                  </Flex>
                ) : (
                  <Flex isColumn={true} gap={2}>
                    {bookmarks.map((bookmark, key) => {
                      return (
                        <BookmarkItem
                          key={key}
                          index={key}
                          onClick={onCloseMenu}
                          selectedBookmark={selectedBookmark}
                          setSelectedBookmark={setSelectedBookmark}
                          bookmark={bookmark}
                        />
                      );
                    })}
                  </Flex>
                )}
              </Flex>
            </>
          )}
          <Flex isColumn={true} gap={6} onClick={onCloseMenu}>
            {pdfUrl && (
              <div
                data-testid="pdf-viewer-container"
                className={pdfContainerClasses}
              >
                <PDFViewer url={pdfUrl} onPassword={async () => ''} />
              </div>
            )}
            {!pdfUrl && (
              <Flex className={styles.pdfContainer} isCentered={true}>
                <Text color="secondary">Loading tax return...</Text>
              </Flex>
            )}
          </Flex>
        </Dialog.Content>
        <Dialog.Footer>
          <ButtonGroup align="right">
            {selectedTaxReturn?.draft_status === 'change_requested' ? (
              <Pill label="Changes Requested" intent="accent" />
            ) : (
              <>
                <Button
                  value="Request Changes"
                  intent="danger-secondary"
                  onClick={() => setShowReasonDialog(true)}
                />
                <Button
                  value="Approve"
                  intent="success"
                  onClick={handleApprove}
                />
              </>
            )}
          </ButtonGroup>
        </Dialog.Footer>
      </Dialog>
      <ReasonDialog
        isOpen={showReasonDialog}
        onClose={() => setShowReasonDialog(false)}
        onSubmit={handleRequestChanges}
      />
    </>
  );
};
